// lastGhost.ts — GHOST DUELS (Slice A) local self-race store. A single best-effort localStorage slot
// holding the player's LAST daily run as a ghost, so "Race yesterday's you" works fully offline with no
// backend and no privacy surface (GHOST_DUELS_BRIEF §11). Presentation/meta only — never touches Core,
// the Sim, or determinism. Tolerant of private-mode / blocked storage exactly like stats.ts/settings.ts.
//
// The codec (ghostTrack.ts) stays pure/DOM-free and smoke-testable; this thin wrapper owns the only
// `window.localStorage` access, so both the recorder (GameScene) and the surface (todayGauntletPanel)
// share one envelope shape + one read/write path.

const KEY = 'trivia.lastGhost.v1';

/** The persisted envelope. `track` is a base64url ghostTrack; the pin fields gate a valid self-race. */
export interface LastGhost {
  v: 1;
  /** GHOST_BALANCE_VERSION at record time — a bv mismatch means the board layout differs → no ghost. */
  bv: string;
  /** UTC day key the run was recorded on (display / staleness only; the topic+seed+m pin is authoritative). */
  day: string;
  /** topicNormalized of the recorded board — must match today's daily topic to replay on the same board. */
  topic: string;
  /** the daily seed the run was recorded under. */
  seed: number;
  /** the daily mutator id the run was recorded under (dailyMutatorId). */
  m: number;
  /** display name (<=12 chars), always 'YOU' for the local self-race. */
  name: string;
  /** the recorded human score (used for the CTA row + as the race target). */
  score: number;
  /** board length in ms (metadata / future use). */
  durationMs: number;
  /** base64url-encoded GhostTrack (ghostTrack.encodeTrack). */
  track: string;
}

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
function str(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

/** Read the persisted last-ghost envelope, or null if absent / corrupt / storage-blocked. */
export function readLastGhost(): LastGhost | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as Partial<LastGhost>;
    if (typeof o !== 'object' || o === null) return null;
    const track = str(o.track, '');
    if (track === '') return null; // no replayable payload → treat as absent
    return {
      v: 1,
      bv: str(o.bv, ''),
      day: str(o.day, ''),
      topic: str(o.topic, ''),
      seed: num(o.seed, 0),
      m: num(o.m, -1),
      name: str(o.name, 'YOU').slice(0, 12) || 'YOU',
      score: num(o.score, 0),
      durationMs: num(o.durationMs, 0),
      track,
    };
  } catch {
    return null;
  }
}

/** Persist the last-ghost envelope (best-effort; a blocked/full store is silently ignored). */
export function writeLastGhost(g: LastGhost): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(g));
  } catch {
    // private mode / blocked / quota — the self-race just won't be available next session.
  }
}
